import React, { useState, useEffect, useCallback } from 'react';
import { permissionAPI } from '../../services/api';
import { Button, Input, Alert, Modal, Loading } from '../../components/common';
import './AdminPages.css';

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filterModule, setFilterModule] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [formLoading, setFormLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    module: '',
    is_active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [permsRes, modulesRes] = await Promise.all([
        permissionAPI.list({ module: filterModule }),
        permissionAPI.modules(),
      ]);
      setPermissions(permsRes.data.results || permsRes.data);
      setModules(modulesRes.data || []);
    } catch (err) {
      setMessage({ type: 'error', text: '获取数据失败' });
    } finally {
      setLoading(false);
    }
  }, [filterModule]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      module: '',
      is_active: true,
    });
    setEditingPermission(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      if (editingPermission) {
        await permissionAPI.update(editingPermission.id, formData);
        setMessage({ type: 'success', text: '权限更新成功' });
      } else {
        await permissionAPI.create(formData);
        setMessage({ type: 'success', text: '权限创建成功' });
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      const errors = err.response?.data;
      const errorMsg = errors?.code?.[0] || errors?.name?.[0] || errors?.detail || '操作失败';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (permission) => {
    setEditingPermission(permission);
    setFormData({
      name: permission.name,
      code: permission.code,
      description: permission.description || '',
      module: permission.module || '',
      is_active: permission.is_active,
    });
    setShowModal(true);
  };

  const handleDelete = async (permission) => {
    if (!window.confirm(`确定要删除权限 "${permission.name}" 吗？`)) {
      return;
    }

    try {
      await permissionAPI.delete(permission.id);
      setMessage({ type: 'success', text: '权限已删除' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: '删除失败' });
    }
  };

  // 按模块分组权限
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const module = perm.module || '未分类';
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(perm);
    return acc;
  }, {});

  if (loading) {
    return <Loading fullScreen text="加载中..." />;
  }

  return (
    <div className="admin-page">
      <div className="page-header">
        <div>
          <h1>权限管理</h1>
          <p>管理系统权限配置</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 18, height: 18 }}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          添加权限
        </Button>
      </div>

      {message.text && (
        <Alert
          type={message.type}
          message={message.text}
          onClose={() => setMessage({ type: '', text: '' })}
        />
      )}

      {/* 模块筛选 */}
      <div className="filter-bar">
        <div className="filter-group">
          <label>按模块筛选：</label>
          <select
            className="filter-select"
            value={filterModule}
            onChange={(e) => setFilterModule(e.target.value)}
          >
            <option value="">全部模块</option>
            {modules.map((module) => (
              <option key={module} value={module}>
                {module}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 权限列表（按模块分组） */}
      <div className="permissions-list">
        {Object.entries(groupedPermissions).map(([module, perms]) => (
          <div key={module} className="permission-module-section">
            <h3 className="module-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 20, height: 20 }}>
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              {module}
              <span className="module-count">({perms.length})</span>
            </h3>
            <div className="permissions-grid">
              {perms.map((perm) => (
                <div key={perm.id} className="permission-card">
                  <div className="permission-card-header">
                    <div className="permission-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      </svg>
                    </div>
                    <div className="permission-info">
                      <h4>{perm.name}</h4>
                      <code className="permission-code">{perm.code}</code>
                    </div>
                  </div>
                  <p className="permission-description">
                    {perm.description || '暂无描述'}
                  </p>
                  <div className="permission-card-footer">
                    <span className={`status-badge ${perm.is_active ? 'active' : 'inactive'}`}>
                      {perm.is_active ? '启用' : '禁用'}
                    </span>
                    <div className="permission-actions">
                      <button
                        className="action-btn"
                        onClick={() => openEditModal(perm)}
                        title="编辑"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                      </button>
                      <button
                        className="action-btn danger"
                        onClick={() => handleDelete(perm)}
                        title="删除"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {Object.keys(groupedPermissions).length === 0 && (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <p>暂无权限数据</p>
            <Button onClick={() => { resetForm(); setShowModal(true); }}>
              创建第一个权限
            </Button>
          </div>
        )}
      </div>

      {/* 添加/编辑权限模态框 */}
      <Modal
        isOpen={showModal}
        onClose={() => { setShowModal(false); resetForm(); }}
        title={editingPermission ? '编辑权限' : '添加权限'}
      >
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-grid">
            <Input
              label="权限名称 *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="如：用户管理"
            />
            <Input
              label="权限编码 *"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value })}
              required
              placeholder="如：user:manage"
              hint="唯一标识，用于代码中检查权限"
            />
          </div>

          <Input
            label="所属模块"
            value={formData.module}
            onChange={(e) => setFormData({ ...formData, module: e.target.value })}
            placeholder="如：用户管理"
            hint="用于权限分组"
          />

          <div className="form-group">
            <label className="form-label">描述</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="权限的详细说明"
            />
          </div>

          <div className="form-group">
            <label className="checkbox-item">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <span>启用权限</span>
            </label>
          </div>

          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </Button>
            <Button type="submit" loading={formLoading}>
              {editingPermission ? '保存' : '创建'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PermissionsPage;

